/**
 * Los límites de la interfaz. UN SOLO SITIO.
 *
 * Cuántas filas se piden, cuántas se enseñan antes de "ver todas", cuántos
 * nombres caben en una alerta. Son decisiones de producto que cambian juntas y
 * que alguien tiene que poder revisar de un vistazo: repartidas por los
 * componentes, cada pantalla acaba con un número distinto puesto a ojo.
 *
 * Los límites del SERVIDOR no están aquí: los pone el backend y los valida él.
 * Estos son los de esta interfaz.
 */

/** Meses que ofrece el selector del panel, contando el actual. */
export const PERIOD_CHOICES = 12;

/**
 * La ventana de chat de Work: ancho, alto y el margen que la separa del borde.
 *
 * El alto es el de partida; si la ventana del navegador no da para tanto, se
 * queda con lo que haya menos el margen. El armazón reserva este ancho cuando
 * está abierta --por eso el número vive aquí y no dentro del JSX-- para que la
 * ventana no tape la paginación de una tabla ni el pie de una pantalla.
 */
export const CHAT_BUBBLE_WIDTH = 380;
export const CHAT_BUBBLE_HEIGHT = 520;
export const CHAT_BUBBLE_MARGIN = 16;
/** El botón redondo cuando está cerrada. El armazón le reserva el pie. */
export const CHAT_BUBBLE_BUTTON = 48;

/**
 * Páginas que se piden, como mucho, al traer ENTERA una lista corta (los lotes
 * del mes, las mecánicas). Con el tope del backend, son cientos de filas: si
 * una lista de estas pasa de ahí, ya no es corta y merece otra pantalla.
 */
export const MAX_LIST_PAGES = 10;

/**
 * El diagrama del proceso de revisión: cuánto mide cada caja.
 *
 * El acomodo (elkjs) necesita los tamaños ANTES de dibujar, así que no se
 * miden: se declaran aquí y las cajas los respetan. Cuando los pasos lleven
 * sus cifras vivas, crecer la caja es cambiar este número.
 */
export const REVIEW_FLOW_STEP = { width: 208, height: 76 } as const;
/**
 * El alto de cada línea de las salidas que se dicen dentro de la caja
 * («No se puede determinar → Revisión manual»), y el aire que las separa.
 */
export const REVIEW_FLOW_EXIT_LINE = 15;
export const REVIEW_FLOW_EXIT_GAP = 6;
/** Una rama dentro de unas validaciones en paralelo: más estrecha, van en fila. */
export const REVIEW_FLOW_BRANCH = { width: 176, height: 68 } as const;
export const REVIEW_FLOW_END = { width: 96, height: 40 } as const;
/** Lo que el bloque en paralelo reserva arriba (su nombre) y abajo (cómo se juntan). */
export const REVIEW_FLOW_GROUP_HEADER = 40;
export const REVIEW_FLOW_GROUP_FOOTER = 44;
/**
 * Nombres de resultado que lleva una flecha antes de rematar con "y N más".
 * El detalle del paso los tiene todos.
 */
export const REVIEW_FLOW_LABEL_LINES = 3;

/** Facturas por página en «Facturas en revisión» (el API admite hasta 100). */
export const INVOICE_REVIEWS_PAGE_SIZE = 30;
/**
 * «Reprocesar todas»: cuántas se piden por página para juntarlas (el tope de
 * la lista) y cuántas van en cada llamada (el tope del lote del API).
 */
export const REPROCESS_COLLECT_PAGE_SIZE = 100;
export const REPROCESS_BATCH_MAX = 200;
/** El zoom de la foto de una factura: de entrada 1, y estos topes. */
export const INVOICE_IMAGE_MIN_ZOOM = 0.5;
export const INVOICE_IMAGE_MAX_ZOOM = 6;

/**
 * La digitación: los recortes de la foto por sección.
 *
 * El margen alrededor de la sección es una fracción del ANCHO de la foto, y
 * vale lo mismo en vertical: así el aire es igual por los cuatro lados aunque
 * la factura sea una tirilla larga.
 */
export const DATA_ENTRY_CROP_MARGIN = 0.03;
/**
 * Una sección muy alta --los ítems de una tirilla larga-- no se encoge hasta
 * caber entera: ocupa al menos esta fracción del ancho del recorte, y el
 * recorte se centra en el ítem que se está digitando.
 */
export const DATA_ENTRY_TALL_FIT = 0.6;
/**
 * El zoom de partida de un recorte busca que el texto SE LEA, no que quepa
 * la sección entera: deja la altura de una línea de texto --el recuadro de
 * un dato-- en estos píxeles de pantalla, con tope `INVOICE_IMAGE_MAX_ZOOM`.
 * Si así la sección cabe, se ve entera como siempre; si no, se ve su
 * comienzo, o el dato con el foco, y lo demás desplazando.
 */
export const DATA_ENTRY_LEGIBLE_LINE_PX = 19;
/** Cuánto acerca cada pulsación de zoom en un recorte. */
export const DATA_ENTRY_ZOOM_STEP = 1.25;
/** Cuánto desplaza cada flecha: una fracción de lo que se ve. */
export const DATA_ENTRY_PAN_STEP = 0.15;
/** Para saltar a una posición de la cola tecleando su número. */
export const DATA_ENTRY_TYPEAHEAD_MS = 900;
/** Facturas por página en la cola de la digitación. */
export const DATA_ENTRY_PAGE_SIZE = 50;
/**
 * Cada cuánto se refrescan solas la cola y la factura abierta. El API no
 * tiene canal en vivo hacia el navegador: así se ve quién está viendo qué,
 * lo que otros decidieron desaparece y la factura abierta se entera de las
 * líneas que asignó un alias nuevo. Es lo más que tarda en enterarse.
 */
export const DATA_ENTRY_REFRESH_MS = 15_000;

/**
 * Pasar a la siguiente al instante (`siguiente-rapida.md`, mecanismos 1 y
 * 5): de cuántas de las siguientes probables se precargan los datos y de
 * cuántas la foto. Con más gente trabajando la misma cola se ensancha
 * --sus tomas se llevan justo las primeras predichas-- hasta el tope. Cada
 * foto decodificada son ~48 MB: solo se decodifica la primera.
 */
export const DATA_ENTRY_PREFETCH_ITEMS = 3;
export const DATA_ENTRY_PREFETCH_ITEMS_MAX = 5;
export const DATA_ENTRY_PREFETCH_PHOTOS = 2;
export const DATA_ENTRY_PREFETCH_PHOTOS_MAX = 3;
/** Cuánto espera el navegador ocioso antes de precargar igual. */
export const DATA_ENTRY_PREFETCH_IDLE_MS = 1500;
/**
 * Girar la foto (PROVISIONAL, ver `RotatePhotoInput`): cuánto se espera tras
 * la última pulsación antes de mandar el giro al API. Varias seguidas solo
 * guardan la final, sin perder ninguna.
 */
export const DATA_ENTRY_ROTATE_DEBOUNCE_MS = 500;
/**
 * Cuánto puede pasarse el subtotal de una línea de cantidad × unitario sin
 * que se avise (redondeos). La del back (`contrato-digitacion.md` §16).
 */
export const LINE_AMOUNTS_TOLERANCE = 0.02;

/** Cuánto hay que quedarse sobre una fila para precargarla. */
export const DATA_ENTRY_PEEK_MS = 150;

/**
 * Deshacer en Digitación (`deshacer.md`). Una decisión espera en el
 * navegador `UNDO_MS` desde que se pulsa, y se puede deshacer mientras
 * tanto; con el ratón o el foco en el botón la cuenta se pausa, y al seguir
 * le quedan al menos `RESUME_MIN_MS`, nunca más de `MAX_MS` desde el
 * principio. Las líneas agregadas o quitadas duran lo mismo en la pila.
 */
export const DATA_ENTRY_UNDO_MS = 10_000;
export const DATA_ENTRY_UNDO_RESUME_MIN_MS = 2_000;
export const DATA_ENTRY_UNDO_MAX_MS = 30_000;
/** Cuántas acciones guarda la pila de deshacer. */
export const DATA_ENTRY_UNDO_DEPTH = 5;
/**
 * Antes de retener una decisión, su toma tiene que durar el tope de la
 * espera más este margen; si no, se renueva.
 */
export const DATA_ENTRY_UNDO_CLAIM_MARGIN_MS = 60_000;
/** Cada cuánto se reintenta una decisión que no llegó (la red, un 5xx). */
export const DATA_ENTRY_FLUSH_RETRY_MS = 3_000;
/** Lo retenido que quedó en la pestaña: se manda si tiene menos que esto. */
export const DATA_ENTRY_HELD_MAX_AGE_MS = 15 * 60_000;
/**
 * El tope de un cuerpo con `keepalive` (64 KiB en el navegador, con margen):
 * uno más grande queda solo en la copia de la pestaña.
 */
export const DATA_ENTRY_KEEPALIVE_MAX_BYTES = 60_000;
/** Cuántas retenidas se saltan en «Tomar siguiente» (`REVIEW_CLAIM_EXCLUDE_MAX`). */
export const DATA_ENTRY_CLAIM_EXCLUDE_MAX = 50;

/**
 * «Acierto»: cuántas semanas o meses se ven de una vez, terminando en el del
 * panel, y cada cuánto se repide (el barrido del worker registra cada
 * minuto).
 */
export const ACCURACY_PERIODS_SHOWN = { week: 8, month: 6 } as const;
export const ACCURACY_REFRESH_MS = 60_000;

/**
 * Cada cuánto se repide una factura en revisión mientras sigue en curso: sus
 * pasos avanzan en el worker y el API no avisa al navegador. Terminada o
 * esperando a una persona, ya no cambia sola y no se repide.
 */
export const INVOICE_REVIEW_LIVE_REFRESH_MS = 5_000;

/**
 * El tablero del proceso: cada cuánto se repide lo que hay en cada cola, y
 * desde cuánto esperar el turno se dice en ámbar. Un paso con IA trabaja
 * unos 5 a 30 s: dos minutos de espera ya son una fila.
 */
export const REVIEW_WORKLOAD_REFRESH_MS = 5_000;
export const REVIEW_QUEUE_SLOW_MS = 120_000;

/**
 * Facturas ▸ Resumen: cada cuánto se repide «¿cómo vamos?». Las cifras
 * cambian con cada factura que se decide, pero nadie las mira segundo a
 * segundo; al minuto, como el acierto.
 */
export const INVOICE_PROGRESS_REFRESH_MS = 60_000;

/** Cuántos digitadores se ven en el Resumen antes de «ver todos». */
export const INVOICE_PROGRESS_REVIEWERS_SHOWN = 8;

/**
 * Lo que se espera, como mucho, a cada llamada de la sesión: el refresh del
 * arranque, `/me`, el canje del login y el logout. En producción el gateway
 * sostenía `/auth/refresh` abierto diez minutos con el API sin contestar, y
 * la aplicación se quedaba en blanco todo ese rato. Pasado esto se dice que
 * no se pudo conectar y se ofrece reintentar.
 */
export const AUTH_TIMEOUT_MS = 10_000;

/**
 * Alfred, el buzón: los topes del contrato de `create_alfred_ticket` (los
 * de `create_suggestion`: título 1..120, descripción 1..4000, la ruta hasta
 * 300). Y cuánto se ve el «Listo» en la cabecera de Work, donde no hay
 * barra de estado.
 */
export const ALFRED_TITLE_MAX = 120;
export const ALFRED_DESCRIPTION_MAX = 4_000;
export const ALFRED_PAGE_MAX = 300;
export const ALFRED_THANKS_MS = 5_000;

/**
 * Los topes de Alfred que pone el API, repetidos aquí para decirlos ANTES de
 * subir nada; el API los vuelve a validar. Salen de su `settings.ts`
 * (`ALFRED_ATTACHMENT_*`) y de su agregado (`ALFRED_COMMENT_MAX`,
 * `ALFRED_NOTE_MAX`), y el tamaño y los tipos están también en la
 * descripción de `uploadAlfredAttachment` en `api-types.ts`. Si allí
 * cambian, se cambian aquí.
 */
export const ALFRED_COMMENT_MAX = 4_000;
/** La nota de un cambio de estado y el motivo de anular. */
export const ALFRED_NOTE_MAX = 1_000;
export const ALFRED_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Adjuntos vivos por pedido, contando los de sus comentarios. */
export const ALFRED_ATTACHMENTS_PER_TICKET_MAX = 30;
/** Los tipos que acepta, con la extensión por la que se reconocen. */
export const ALFRED_ATTACHMENT_TYPES: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "text/plain": "txt",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
};

/** Lo que espera el buscador de participantes tras la última tecla. */
export const DATA_ENTRY_QUEUE_SEARCH_MS = 300;

/** Cuántos SKU trae lo que el selector pide al API al teclear. */
export const CATALOG_SEARCH_LIMIT = 10;
/**
 * El catálogo entero se pide al abrir el selector (391 participantes) para
 * filtrarlo en el navegador al instante; el API recorta a su tope.
 */
export const CATALOG_WHOLE_LIMIT = 500;
/**
 * Desde cuántos caracteres lo escrito se busca también DENTRO del código de
 * lala4 (con menos, solo por donde empieza): la misma cifra que el API.
 */
export const CATALOG_CODE_CONTAINS_MIN = 4;
/** Lo buscado en el catálogo se reusa este rato (cambia poco). */
export const CATALOG_SEARCH_STALE_MS = 5 * 60_000;
/** Lo que se espera tras la última tecla antes de buscar en el catálogo. */
export const CATALOG_SEARCH_DEBOUNCE_MS = 200;

/** Cuántas facturas entran, como mucho, en una vista previa del lote. */
export const DATA_ENTRY_BATCH_MAX = 100;

/** `DataTable`: filas por página de partida, y lo que ofrece para cambiarla. */
export const DATA_TABLE_PAGE_SIZE = 10;
export const DATA_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
/** Filas de esqueleto mientras carga, si no se sabe aún cuántas vienen. */
export const DATA_TABLE_SKELETON_ROWS = 5;
